import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { SearchAutocomplete } from '@/shared/ui/components/form/SearchAutocomplete';
import { SegmentedControl, Text, Textarea } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { useAgencyClients } from '@/features/visits/hooks/useAgencyClients';
import { ClientOpenAttendanceWarning } from './ClientOpenAttendanceWarning';
import { useAgencyProperties } from '@/features/visits/hooks/useAgencyProperties';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';
import { isDevFormBridgeEnabled } from '@/shared/utils/dev-form-bridge';

interface CreateDealDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateDealFormData) => Promise<void>;
  agencyId: string;
  /** Pre-selects a client (e.g. "Criar negócio" from the client detail page). */
  initialClientId?: string | null;
}

/** The four states a client can arrive in. */
export type DeclaredInterest = 'buy' | 'rent' | 'both' | 'unsure';

export interface CreateDealFormData {
  /**
   * What the broker reads the client as wanting — a guess, edited as
   * they learn. Deliberately *not* `deal_type`: this form used to force
   * a choice between sale and rental at opening, which a client who
   * arrives undecided cannot honestly make. The type is decided by the
   * first offer instead.
   */
  interest: DeclaredInterest;
  client_id: string;
  property_id: string;
  notes: string;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * Adaptive form shell for creating a new deal.
 *
 * Uses SearchAutocomplete for client and property selection,
 * matching the VisitDrawer pattern.
 *
 * @param props - Dialog open state, callbacks, and agency context.
 * @returns The create deal form shell.
 */
export function CreateDealDialog({
  open,
  onClose,
  onSubmit,
  agencyId,
  initialClientId = null,
}: CreateDealDialogProps) {
  const { t } = useTranslation('features/deals');
  const [interest, setInterest] = useState<DeclaredInterest>('unsure');
  const [clientId, setClientId] = useState<string | null>(initialClientId);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  // An attendance follows a person: it is the record of working *for*
  // somebody, and nothing downstream works without one — a won
  // attendance with no client cannot open a Sale, because a sale needs
  // a buyer. The property is the opposite case and stays optional: the
  // agency may not have shown anything yet.
  const canSubmit = Boolean(clientId);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setClientId(initialClientId);
  }, [open, initialClientId]);

  const { clients } = useAgencyClients(agencyId);
  const { properties } = useAgencyProperties(agencyId);

  const clientOptions = clients.map((c) => ({
    id: c.id,
    label: c.label,
    sublabel: c.sublabel,
  }));

  const propertyOptions = properties.map((p) => ({
    id: p.id,
    label: p.label,
    sublabel: p.sublabel,
  }));

  useEffect(() => {
    if (isDevFormBridgeEnabled() && open) {
      (window as any).__DEV_DEAL_FORM__ = { submit: handleSubmit };
    }
    return () => { (window as any).__DEV_DEAL_FORM__ = undefined; };
  }, [open]);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        interest,
        client_id: clientId ?? '',
        property_id: propertyId ?? '',
        notes,
      });
      setClientId(null);
      setPropertyId(null);
      setNotes('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t('newDeal')}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={() => handleSubmit()}
          disabled={submitting || !canSubmit}
          label={t('form.create')}
          savingLabel={t('form.creating')}
        />
      }
    >
      <div
        data-testid="create-deal-form"
        className="flex flex-col gap-(--space-4)"
      >
        <fieldset className="flex flex-col items-center gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('form.interest')}
          </Text>
          <SegmentedControl
            value={interest}
            onChange={(v) => setInterest(v as DeclaredInterest)}
            items={[
              { value: 'buy', label: t('form.interests.buy') },
              { value: 'rent', label: t('form.interests.rent') },
              { value: 'both', label: t('form.interests.both') },
              { value: 'unsure', label: t('form.interests.unsure') },
            ]}
            size="md"
            testId="interest-selector"
            className="self-center"
          />
          <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
            {t('form.interestHint')}
          </Text>
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('form.client')} <Text as="span" className="text-[var(--status-error)]">*</Text>
          </Text>
          <SearchAutocomplete
            options={clientOptions}
            value={clientId}
            onChange={setClientId}
            placeholder={t('form.clientPlaceholder')}
          />
          {/* Stated rather than left to a disabled button the reader
              has to explain to themselves. The property below carries
              no such note on purpose: it really is optional. */}
          <Text
            component="span"
            data-testid="create-deal-client-hint"
            className="text-[length:var(--text-meta)] leading-normal text-(--text-tertiary)"
          >
            {t('form.clientHint')}
          </Text>
          {/* After the hint rather than before the field: it answers a
              question the reader only has once they have picked
              somebody, and above the input it would be a warning about
              nobody. */}
          <ClientOpenAttendanceWarning agencyId={agencyId} clientId={clientId} />
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClasses}>
            {t('form.property')}
          </Text>
          <SearchAutocomplete
            options={propertyOptions}
            value={propertyId}
            onChange={setPropertyId}
            placeholder={t('form.propertyPlaceholder')}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" htmlFor="deal-notes" className={labelClasses}>
            {t('form.notes')}
          </Text>
          <Textarea
            id="deal-notes"
            data-testid="deal-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('form.notesPlaceholder')}
            rows={3}
          />
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
